"use client";

import { XIcon } from "lucide-react";
import { isAddress } from "viem";
import { useEffect, useState } from "react";

import type { TokenInfo } from "@/domain/types";
import { useCustomTokenInfo } from "@/hooks/useCustomTokenInfo";
import { useTokenList } from "@/hooks/useTokenList";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { TokenAvatar } from "./TokenAvatar";

const CUSTOM_VALUE = "__custom__";

export function TokenSelect({ value, onChange }: { value: TokenInfo | null; onChange: (t: TokenInfo) => void }) {
  const { data: tokens = [] } = useTokenList();
  const [customMode, setCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const { token: customToken, loading: customLoading, notFound: customNotFound } = useCustomTokenInfo(customInput);

  useEffect(() => {
    if (!customToken) return;
    onChange(customToken);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCustomMode(false);
    setCustomInput("");
    // Only react to a newly-resolved token, not to onChange identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customToken]);

  return (
    <div className="flex items-center gap-2">
      {value && <TokenAvatar symbol={value.symbol} address={value.address} logoUrl={value.logoUrl} size={28} />}
      {!customMode ? (
        <Select
          value={value?.address ?? ""}
          onValueChange={(v) => {
            if (v === CUSTOM_VALUE) {
              setCustomMode(true);
              return;
            }
            const t = tokens.find((tok) => tok.address === v);
            if (t) onChange(t);
          }}
        >
          <SelectTrigger
            className="min-w-[min(7rem,32vw)] transition-colors"
            aria-label={value ? `Selected token: ${value.symbol}` : "Select token"}
          >
            {/* Base UI's Select.Value (unlike Radix's) needs an explicit
                render function to format the label — it shows the raw
                value string otherwise. */}
            <SelectValue placeholder="Select token">{() => value?.symbol ?? "Select token"}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {tokens.map((t) => (
              <SelectItem key={t.address} value={t.address}>
                <span className="flex items-center gap-2">
                  <TokenAvatar symbol={t.symbol} address={t.address} logoUrl={t.logoUrl} size={18} />
                  {t.symbol}
                </span>
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM_VALUE}>Custom token…</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1">
            <Input
              className="w-40 text-xs"
              placeholder="Token address 0x…"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              autoFocus
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Cancel custom token"
              onClick={() => {
                setCustomMode(false);
                setCustomInput("");
              }}
            >
              <XIcon />
            </Button>
          </div>
          {customLoading && <span className="text-[0.7rem] text-muted-foreground">Resolving…</span>}
          {customNotFound && (
            <span className="text-[0.7rem] text-destructive">Not a valid ERC-20 token</span>
          )}
          {customInput && !isAddress(customInput) && !customLoading && (
            <span className="text-[0.7rem] text-muted-foreground">Enter a full token address</span>
          )}
        </div>
      )}
    </div>
  );
}
